import { type PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { DTOProfileCard } from 'src/apis/dto/DTOProfileCard';
import ModalLayoutBase, { MobileModalType, ModalContent as ModalContentBase, ModalLayoutProps } from 'src/components/ModalLayout';
import { UseGetProfileCardQueryParams, useGetProfileCardQuery } from 'src/hooks/useProfileQuery';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import ProfileInfo from './ProfileInfo';
import ProfileTop from './ProfileTop';
import ScrollIndicator from './ScrollIndicator';
const fadeInTop = keyframes`
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0%);
  }
`;
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
  min-width: auto;
  max-width: 400px;
  ${({
  theme
}) => theme.screenSize.mobile} {
    width: calc(var(--100vw) - 32px);
    max-width: 100%;
    bottom: calc(var(--dvh) * 10);
    border-radius: 16px;
    animation: ${fadeInTop} 0.4s ease-in-out;
  }
`;
const ModalContent = styled(ModalContentBase)`
  overflow-x: hidden;
  max-height: calc(80 * var(--dvh));
  aspect-ratio: 343 / 563;
  ${({
  theme
}) => theme.screenSize.mobile} {
    aspect-ratio: unset;
    height: calc(80 * var(--dvh));
  }
  padding: 0;
  border-radius: 16px;
`;
const profileCardContext = createContext<{
  profile: DTOProfileCard;
  eventTab?: string;
  eventPage?: string;
  eventSection?: string;
  onReportSuccess?: () => void;
} | null>(null);
export const useProfileCardContext = () => {
  const context = useContext(profileCardContext);
  if (!context) {
    throw new Error('useProfileCardContext must be used within a ProfileCardProvider');
  }
  return context;
};
type ProfileModalEventPage = 'main' | 'match' | 'following' | 'follower' | 'message_room' | 'viewed_me' | 'swipe_invite';
type Props = {
  anchor?: 'hashtags';
  buttons?: React.ReactElement;
  eventTab?: string;
  eventPage?: ProfileModalEventPage;
  eventSection?: string;
  onReportSuccess?: () => void;
} & ModalLayoutProps & UseGetProfileCardQueryParams;
const ProfileModal = (props: PropsWithChildren<Props>) => {
  const {
    data: profile
  } = useGetProfileCardQuery(props);
  const {
    selectedTab
  } = useSelectedTab();
  const hasLoggedRef = useRef(false);
  const {
    anchor,
    buttons,
    children,
    eventTab,
    eventPage,
    eventSection = 'none',
    onReportSuccess
  } = props;
  const tab = eventTab ?? selectedTab.name;
  useEffect(() => {
    if (profile && profile.type !== 'MY_PROFILE' && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__CLICK_OTHER_PROFILE,
        eventParams: {
          action_category: 'click',
          tab,
          page: eventPage,
          section: eventSection,
          target: 'other_profile',
          card_id: profile.id
        }
      });
    }
  }, [profile, tab, eventPage, eventSection]);
  if (profile == null) {
    return;
  }
  return <ModalLayout mobileType={MobileModalType.BOTTOM_SHEET} lockBodyScroll isDim data-testid='profile-modal' data-sentry-element="ModalLayout" data-sentry-component="ProfileModal" data-sentry-source-file="index.tsx">
      <profileCardContext.Provider value={{
      profile,
      eventTab: tab,
      eventPage,
      eventSection,
      onReportSuccess
    }} data-sentry-element="profileCardContext.Provider" data-sentry-source-file="index.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
          <ProfileTop profile={profile} data-sentry-element="ProfileTop" data-sentry-source-file="index.tsx" />
          <ScrollIndicator data-sentry-element="ScrollIndicator" data-sentry-source-file="index.tsx" />
          <ProfileInfo anchor={anchor} data-sentry-element="ProfileInfo" data-sentry-source-file="index.tsx" />
          {children}
        </ModalContent>
        {buttons}
      </profileCardContext.Provider>
    </ModalLayout>;
};
export default ProfileModal;